import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  ArrowLeft, 
  Play, 
  Clock, 
  Target, 
  CheckCircle2, 
  Lock,
  Star,
  Users,
  Trophy
} from 'lucide-react';
import { getTrackById } from '@/data/learningTracks';
import { useLearningProgress } from '@/hooks/useLearningProgress';
import { toast } from '@/hooks/use-toast';
import { UnifiedCourseService } from '@/services/unifiedCourseService';

export const LearningPathDetail: React.FC = () => {
  const { trackId } = useParams<{ trackId: string }>();
  const navigate = useNavigate();
  const { trackProgress, canUserAccessCourse } = useLearningProgress();
  
  const track = trackId ? getTrackById(trackId) : undefined;
  const progress = trackProgress.find(p => p.trackId === trackId);

  if (!track) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="text-center py-12">
          <CardContent>
            <h2 className="text-2xl font-bold mb-4">Track Not Found</h2>
            <p className="text-muted-foreground mb-6">
              The learning track you're looking for doesn't exist.
            </p>
            <Button onClick={() => navigate('/learning-paths')}>
              Back to Learning Paths
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleStartPathway = async (pathwayId: string) => {
    const pathway = track.pathways.find(p => p.id === pathwayId);
    if (pathway && pathway.courseIds.length > 0) {
      // Validate course exists before navigating
      const firstCourseId = pathway.courseIds[0];
      const course = await UnifiedCourseService.getCourseById(firstCourseId);
      
      if (course) {
        navigate(`/courses/${firstCourseId}`);
      } else {
        toast({
          title: "Course Not Available",
          description: `The course "${firstCourseId}" is not available yet. Please try another pathway.`,
          variant: "destructive"
        });
      }
    }
  };

  const handleContinuePathway = async (pathwayId: string) => {
    const pathway = track.pathways.find(p => p.id === pathwayId);
    if (pathway) {
      // Find first available course in pathway
      for (const courseId of pathway.courseIds) {
        const course = await UnifiedCourseService.getCourseById(courseId);
        if (course && (!progress?.unlockedCourses.includes(courseId) || canUserAccessCourse(courseId))) {
          navigate(`/courses/${courseId}`);
          return;
        }
      }
      
      toast({
        title: "No Available Courses",
        description: "No courses are currently available in this pathway.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/learning-paths')}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Learning Paths
        </Button>
      </div>

      {/* Track Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-6">
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl"
              style={{ backgroundColor: `${track.color}15`, color: track.color }}
            >
              {track.icon}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <CardTitle className="text-3xl font-bold">{track.name}</CardTitle>
                <Badge variant="secondary" className="text-sm">
                  {track.type.charAt(0).toUpperCase() + track.type.slice(1)}
                </Badge>
              </div>
              <p className="text-lg text-muted-foreground mb-4">
                {track.description}
              </p>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">{track.estimatedDuration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">{track.pathways.length} Pathways</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">15,000+ Students</span>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        
        {progress && progress.overallProgress > 0 && (
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="font-medium">Overall Progress</span>
                <span className="text-sm font-semibold">{progress.overallProgress}%</span>
              </div>
              <Progress value={progress.overallProgress} className="h-3" />
              <p className="text-sm text-muted-foreground">
                {progress.completedPathways.length} of {track.pathways.length} pathways completed
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Learning Pathways */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Learning Pathways</h2>
        
        <div className="space-y-4">
          {track.pathways.map((pathway, index) => {
            const isCompleted = progress?.completedPathways.includes(pathway.id);
            const isAccessible = !pathway.prerequisites || 
              pathway.prerequisites.every(prereq => 
                progress?.completedPathways.includes(prereq)
              );
            const isCurrent = progress?.currentPathway === pathway.id;

            return (
              <Card 
                key={pathway.id} 
                className={`transition-all duration-200 ${
                  isCurrent ? 'border-primary bg-primary/5' : ''
                } ${!isAccessible ? 'opacity-60' : ''}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle className="text-xl">{pathway.name}</CardTitle>
                          {isCompleted && (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          )}
                          {!isAccessible && (
                            <Lock className="w-5 h-5 text-muted-foreground" />
                          )}
                          <Badge 
                            variant={pathway.level === 'foundation' ? 'secondary' : 'outline'}
                            className="capitalize"
                          >
                            {pathway.level}
                          </Badge>
                        </div>
                        
                        <p className="text-muted-foreground mb-3">
                          {pathway.description}
                        </p>

                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {pathway.estimatedDuration}
                          </span>
                          <span className="flex items-center gap-1">
                            <Target className="w-4 h-4" />
                            {pathway.courseIds.length} Courses
                          </span>
                        </div>

                        {/* Skills */}
                        <div className="space-y-2">
                          <span className="text-sm font-medium">Key Skills:</span>
                          <div className="flex flex-wrap gap-1">
                            {pathway.skills.slice(0, 4).map((skill) => (
                              <Badge key={skill} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {pathway.skills.length > 4 && (
                              <Badge variant="secondary" className="text-xs">
                                +{pathway.skills.length - 4} more
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Capstone Project */}
                        {pathway.capstoneProject && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Trophy className="w-4 h-4 text-primary" />
                              <span className="font-medium text-sm">Capstone Project</span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {pathway.capstoneProject.title}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      {isAccessible && !isCompleted && (
                        <Button 
                          onClick={() => isCurrent ? 
                            handleContinuePathway(pathway.id) : 
                            handleStartPathway(pathway.id)
                          }
                          size="sm"
                          className="gap-2"
                        >
                          <Play className="w-4 h-4" />
                          {isCurrent ? 'Continue' : 'Start'}
                        </Button>
                      )}
                      
                      {isCompleted && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleStartPathway(pathway.id)}
                          className="gap-2"
                        >
                          <Star className="w-4 h-4" />
                          Review
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Enrollment CTA */}
      {!progress || progress.overallProgress === 0 ? (
        <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
          <CardContent className="text-center py-8">
            <h3 className="text-2xl font-bold mb-4">Ready to Start Your {track.name} Journey?</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Join thousands of students who have mastered {track.name} through our structured learning approach.
            </p>
            <Button 
              size="lg" 
              onClick={() => handleStartPathway(track.pathways[0]?.id)}
              className="gap-2"
            >
              <Play className="w-5 h-5" />
              Start Learning Now
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};